import { supabase } from '../../supabase';
import { ensureGymId } from '../receptionist/gymContext';
import { paymentMethodMeta } from '../../pages/admin/payment-methods/paymentMethodsData';

const DEFAULT_CONFIG = {
  bankName: '',
  accountName: '',
  accountNumber: '',
  iban: '',
  jazzCashNumber: '',
  easyPaisaNumber: '',
  paymentInstructions: '',
};

function rowToGateway(row) {
  return {
    id: row.id,
    methodKey: row.method_key,
    label: row.label,
    description: row.description || '',
    icon: row.icon || '',
    connectType: row.connect_type || 'manual',
    enabled: Boolean(row.enabled),
    apiConnected: Boolean(row.api_connected),
    config: { ...DEFAULT_CONFIG, ...(row.config || {}) },
    sortOrder: row.sort_order || 0,
  };
}

function gatewayToRow(gateway, gymId) {
  return {
    gym_id: gymId,
    method_key: gateway.methodKey,
    label: gateway.label,
    description: gateway.description || null,
    icon: gateway.icon || null,
    connect_type: gateway.connectType || 'manual',
    enabled: Boolean(gateway.enabled),
    api_connected: Boolean(gateway.apiConnected),
    config: gateway.config || {},
    sort_order: gateway.sortOrder || 0,
    updated_at: new Date().toISOString(),
  };
}

function throwIfPaymentGatewaysTableMissing(error) {
  if (error && /payment_gateways.*schema cache|relation.*payment_gateways.*does not exist/i.test(error.message)) {
    throw new Error(
      'Payment gateways table not found. Open Supabase → SQL Editor, run supabase/fix_payment_gateways.sql, then refresh.',
    );
  }
}

function isDuplicateKeyError(error) {
  return /duplicate key|unique constraint|payment_gateways_gym_method_uq/i.test(error?.message || '');
}

async function loadPaymentGatewaysForGym(gymId) {
  const { data, error } = await supabase
    .from('payment_gateways')
    .select('*')
    .eq('gym_id', gymId)
    .order('sort_order', { ascending: true });

  if (error) {
    throwIfPaymentGatewaysTableMissing(error);
    throw error;
  }

  return (data || []).map(rowToGateway);
}

async function seedDefaultGateways(gymId) {
  const rows = paymentMethodMeta.map((method, index) => ({
    gym_id: gymId,
    method_key: method.key,
    label: method.label,
    description: method.description,
    icon: method.icon,
    connect_type: method.connectType || 'manual',
    enabled: ['cash', 'card', 'bankTransfer', 'jazzCash'].includes(method.key),
    api_connected: false,
    config: {},
    sort_order: index,
    updated_at: new Date().toISOString(),
  }));

  const { error } = await supabase
    .from('payment_gateways')
    .upsert(rows, { onConflict: 'gym_id,method_key', ignoreDuplicates: true });

  if (error && !isDuplicateKeyError(error)) {
    throwIfPaymentGatewaysTableMissing(error);
    throw error;
  }
}

async function ensureMissingGateways(gymId, existing) {
  const existingKeys = new Set(existing.map((gateway) => gateway.methodKey));
  const missing = paymentMethodMeta
    .filter((method) => !existingKeys.has(method.key))
    .map((method, index) => ({
      gym_id: gymId,
      method_key: method.key,
      label: method.label,
      description: method.description,
      icon: method.icon,
      connect_type: method.connectType || 'manual',
      enabled: ['cash', 'card', 'bankTransfer', 'jazzCash'].includes(method.key),
      api_connected: false,
      config: {},
      sort_order: existing.length + index,
      updated_at: new Date().toISOString(),
    }));

  if (!missing.length) return;

  const { error } = await supabase
    .from('payment_gateways')
    .upsert(missing, { onConflict: 'gym_id,method_key', ignoreDuplicates: true });

  if (error && !isDuplicateKeyError(error)) {
    throwIfPaymentGatewaysTableMissing(error);
    throw error;
  }
}

export async function fetchPaymentGateways() {
  const gymId = await ensureGymId();
  let gateways = await loadPaymentGatewaysForGym(gymId);

  if (!gateways.length) {
    await seedDefaultGateways(gymId);
    gateways = await loadPaymentGatewaysForGym(gymId);
  } else {
    await ensureMissingGateways(gymId, gateways);
    gateways = await loadPaymentGatewaysForGym(gymId);
  }

  return gateways;
}

export async function savePaymentGateway(gateway) {
  const gymId = await ensureGymId();
  const row = gatewayToRow(gateway, gymId);

  if (gateway.id) {
    const { data, error } = await supabase
      .from('payment_gateways')
      .update(row)
      .eq('id', gateway.id)
      .select('*')
      .single();

    if (error) {
      throwIfPaymentGatewaysTableMissing(error);
      throw error;
    }

    return rowToGateway(data);
  }

  const { data, error } = await supabase
    .from('payment_gateways')
    .upsert(row, { onConflict: 'gym_id,method_key' })
    .select('*')
    .single();

  if (error) {
    throwIfPaymentGatewaysTableMissing(error);
    throw error;
  }

  return rowToGateway(data);
}

export async function updatePaymentGatewayEnabled(id, enabled) {
  const { data, error } = await supabase
    .from('payment_gateways')
    .update({ enabled: Boolean(enabled), updated_at: new Date().toISOString() })
    .eq('id', id)
    .select('*')
    .single();

  if (error) {
    throwIfPaymentGatewaysTableMissing(error);
    throw error;
  }

  return rowToGateway(data);
}

export function getMergedConfig(gateways) {
  return gateways.reduce((acc, gateway) => ({ ...acc, ...(gateway.config || {}) }), { ...DEFAULT_CONFIG });
}

export function getMethodsMap(gateways) {
  return gateways.reduce((acc, gateway) => {
    acc[gateway.methodKey] = gateway.enabled;
    return acc;
  }, {});
}

export function getApiConnectionsMap(gateways) {
  return gateways.reduce((acc, gateway) => {
    if (gateway.connectType === 'api') {
      acc[gateway.methodKey] = gateway.apiConnected;
    }
    return acc;
  }, {});
}

export function findGateway(gateways, methodKey) {
  return gateways.find((g) => g.methodKey === methodKey);
}
